import { useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { avatarHueSchema } from '@/api/common.ts';
import type { Workspace, WorkspaceInput } from '@/api/generated/schemas.ts';
import { getWorkspacePath, workspaceSlugSchema } from '@/api/workspaces.ts';
import AvatarColorPicker from '@/avatars/AvatarColorPicker.tsx';
import Form from '@/core/components/Form.tsx';
import SlugPath from '@/core/components/SlugPath.tsx';
import StringField from '@/core/components/StringField.tsx';
import { requiredText } from '@/utils/forms.ts';
import { useAutoSlug } from '@/utils/slugs.ts';

export const workspaceFormSchema = z.object({
    name: requiredText,
    slug: workspaceSlugSchema,
    color_hue: avatarHueSchema.nullable(),
});
export type WorkspaceFormValues = z.input<typeof workspaceFormSchema>;
export type WorkspaceFormOutput = z.output<typeof workspaceFormSchema>;

export const getWorkspaceFormDefaults = (workspace: Workspace | null): WorkspaceFormValues => ({
    name: workspace?.name ?? '',
    slug: workspace?.slug ?? '',
    color_hue: workspace?.color_hue ?? null,
});

export const toWorkspaceInput = (values: WorkspaceFormOutput): WorkspaceInput => ({
    name: values.name,
    slug: values.slug,
    color_hue: values.color_hue,
});

interface WorkspaceFormFieldsProps {
    // For new workspaces: the slug follows the name until it's edited by hand.
    hasAutoSlug?: boolean;
    // Without the avatar's colour, e.g. in the setup, where its empty avatar reads as an image to upload.
    hasColor?: boolean;
}

const WorkspaceFormFields = ({ hasAutoSlug = false, hasColor = true }: WorkspaceFormFieldsProps) => {
    const { t } = useTranslation();
    const handleNameChange = useAutoSlug(hasAutoSlug);
    const name = useWatch<WorkspaceFormValues, 'name'>({ name: 'name' });
    const slug = useWatch<WorkspaceFormValues, 'slug'>({ name: 'slug' });

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
                {hasColor && (
                    <Form.Field name="color_hue">
                        {/* Lined up with the input below the label. */}
                        <AvatarColorPicker avatarName={name} className="mt-6.5" />
                    </Form.Field>
                )}
                <Form.Field name="name">
                    <StringField
                        label={t('workspaces.fields.name')}
                        helpText={t('workspaces.fields.nameHint')}
                        isRequired
                        autoFocus
                        onChange={handleNameChange}
                    />
                </Form.Field>
            </div>
            <Form.Field name="slug">
                <StringField
                    label={t('workspaces.fields.slug')}
                    helpText={
                        <Trans
                            i18nKey="workspaces.fields.slugHint"
                            components={{ path: <SlugPath path={getWorkspacePath(slug || '…')} /> }}
                        />
                    }
                    isRequired
                />
            </Form.Field>
        </div>
    );
};

export default WorkspaceFormFields;
